import React, { useCallback, useState, useEffect } from "react";
import EditItemView from "../EditItemView";
import { FEATURE } from "../../model/features";
import { Form, Modal } from "react-bootstrap";
import ScheduleName from "../../components/ScheduleName";
import { OPERATING_DAY_DEFAULTS, OperatingDays, Period, PREDEFINED_SCHEDULES, Exception, Schedule } from "../../model/schedule";
import util from "util";
import { DatePicker, Select, Row, Col, Button, Tag, Divider, Switch } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import { capitalize, update } from "lodash";
import { publicScheduleModelData, scheduleModelData } from "../../services/ModelService";
import ScheduleCalendar from "../../components/ScheduleCalendar";
import { ReactComponent as Close } from "../../assets/icons/Close.svg";
import RangeOrPrefilledPicker from './RangeOrPrefilledPicker';
import { useAppContext } from "../../libs/contextLib";

const { Option } = Select
const { RangePicker } = DatePicker;

function EditScheduleView(props) {
    const { isAdmin, publicSchedules, privateSchedules } = useAppContext();
    const allTags = props.allTags || [];

    const [schedule, setSchedule] = useState(() => {
        const initialSchedule = props.editing;
        // Add a default exception if there are none
        if (!initialSchedule.exceptions || initialSchedule.exceptions.length === 0) {
            initialSchedule.exceptions = [new Exception({ type: 'include' })];
        }
        return initialSchedule;
    });
    const [showPreview, setShowPreview] = useState(false);
    const [runningDates, setRunningDates] = useState([]);

    // Helper to update schedule and notify parent for live updates
    const updateScheduleAndNotify = (updater) => {
        setSchedule(prev => {
            const updated = updater(typeof prev === 'function' ? prev() : prev);
            if (props.handleItemUpdate) props.handleItemUpdate(updated, { isFinal: false });
            return updated;
        });
    };

    // Update running dates whenever schedule changes
    useEffect(() => {
        const scheduleInstance = new Schedule(schedule);
        setRunningDates(scheduleInstance.getRunningDates(scheduleInstance.getFirstActiveDate()));
    }, [schedule]);

    // Helper to get all prefilled periods (from PREDEFINED_SCHEDULES only, one entry per schedule)
    const getPrefilledPeriods = () => {
        return Object.values({...PREDEFINED_SCHEDULES, ...publicSchedules, ...privateSchedules})
            .filter(sched => sched.schedulePeriods?.length)
            .map(sched => ({
                label: sched.scheduleName,
                scheduleId: sched.scheduleId,
                schedule: sched
            }));
    };

    // Helper to get all prefilled periods (from PREDEFINED_SCHEDULES only)
    const prefilledPeriods = getPrefilledPeriods();

    // Add exception handler
    const handleAddException = (type) => {
        updateScheduleAndNotify(schedule => ({
            ...schedule,
            exceptions: [...(schedule.exceptions || []), new Exception({ type })]
        }));
    };

    const handleAddPeriods = () => {
        updateScheduleAndNotify(schedule => ({
            ...schedule,
            schedulePeriods: [...(schedule.schedulePeriods || []), new Period()]
        }));
    };

    return (
        <>
            <EditItemView item={schedule} feature={FEATURE.schdls} modelData={schedule.shared? publicScheduleModelData : scheduleModelData} {...props}
            handleItemUpdate={(item) => {
                console.log('handleItemUpdate', item)
                props.handleItemUpdate(item, { isFinal: true });
            }}>
                <Row gutter={[30, 30]}>
                    <Col xs={24}>
                        <Row gutter={[10, 15]}>
                            {/* Admin-only Shared Toggle */}
                            {isAdmin && (
                                <Col xs={24} lg={24}>
                                    <Form.Group className="mb-0 mt-2 d-flex align-items-center justify-content-between" controlId={schedule.scheduleId + '_SharedToggle'}>
                                        <Form.Label className="mb-0">Public (Shared)</Form.Label>
                                        <Switch
                                            checked={!!schedule.shared}
                                            onChange={checked => {
                                                updateScheduleAndNotify(schedule => ({ ...schedule, shared: checked }));
                                            }}
                                            checkedChildren="Public"
                                            unCheckedChildren="Private"
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            <Col xs={24} lg={24}>
                                <Form.Group className="mb-0" controlId={schedule.scheduleId + '_ScheduleName'}>
                                    <Form.Label className="mb-0">Schedule Name</Form.Label>
                                    <Form.Control type="text" size="sm" placeholder="Calendar name" className="w-100" disabled={schedule.shared && !isAdmin} value={schedule.scheduleName || ''}
                                        onChange={(e) => {
                                            updateScheduleAndNotify(schedule => ({ ...schedule, scheduleName: e.target.value }));
                                        }} />
                                </Form.Group>
                            </Col>
                            {/* Tags Field */}
                            <Col xs={24} lg={24}>
                                <Form.Group className="mb-0 d-flex flex-column" controlId={schedule.scheduleId + '_Tags'}>
                                    <Form.Label className="mb-0">Tags</Form.Label>
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        placeholder="Enter tags"
                                        value={schedule.tags || []}
                                        onChange={(tags) => {
                                            updateScheduleAndNotify(schedule => ({ ...schedule, tags }));
                                        }}
                                        options={allTags.map(tag => ({ value: tag, label: tag }))}
                                    />
                                </Form.Group>
                            </Col>
                            {/* Base Calendar Section */}
                            {!schedule.shared && <Col xs={24} lg={24}>
                                <Form.Group className="mb-0" controlId={schedule.scheduleId + '_SchedulePeriods'}>
                                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '5px' }}>
                                        <Form.Label style={{ display: 'block', marginBottom: '-4px' }}>Base Schedule</Form.Label>
                                    </div>
                                    <RangeOrPrefilledPicker
                                        schedule={schedule}
                                        // disabled={schedule.shared && !isAdmin}
                                        prefilledPeriods={prefilledPeriods}
                                        value={schedule.schedulePeriods?.length ? schedule.schedulePeriods[0] : schedule.includedSchedules?.length ? schedule.includedSchedules[0] : null}
                                        onChange={(val) => {
                                            if (val.type === 'range') {
                                                updateScheduleAndNotify(schedule => ({
                                                    ...schedule,
                                                    schedulePeriods: [new Period(val.range)],
                                                    includedSchedules: [],
                                                }))
                                            } else if (val.type === 'prefilled') {
                                                updateScheduleAndNotify(schedule => ({
                                                    ...schedule,
                                                    includedSchedules: [val.prefilled.schedule],
                                                    schedulePeriods: []
                                                }))
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Col> }
                            {/* Operating Days Section */}
                            {!schedule.shared && <Col xs={24} lg={24}>
                                    <Form.Group className="mb-0" controlId="operatingDays">
                                        <Form.Label className="mb-0">Operating Days</Form.Label>
                                        <div className={'mb-2'}>
                                            <Select disabled={schedule.shared && !isAdmin} className={'w-100'} style={{ maxWidth: '500px' }} value={'none'}
                                                onChange={(val, el) => {
                                                    updateScheduleAndNotify(schedule => ({ ...schedule, operatingDays: new OperatingDays(OPERATING_DAY_DEFAULTS[val]) }));
                                                    el.value = 'none'
                                                }}>
                                                <Option key={`choose-op`} value={'none'}>Operating days</Option>
                                                {Object.keys(OPERATING_DAY_DEFAULTS).map(type => {
                                                    return <Option key={`OD-${type}`} value={type}>{type}</Option>
                                                })}
                                            </Select>
                                        </div>
                                        <div className="filter-switches switches-sm">
                                            {Object.keys(schedule.operatingDays).map(day => (
                                                <Switch
                                                    key={schedule.scheduleId + '_op_day_' + day}
                                                    disabled={schedule.shared && !isAdmin}
                                                    checked={schedule.operatingDays[day]}
                                                    onChange={(checked) => {
                                                        updateScheduleAndNotify(schedule => ({
                                                            ...schedule,
                                                            operatingDays: { ...schedule.operatingDays, [day]: checked }
                                                        }));
                                                    }}
                                                    size="small"
                                                    style={{ marginRight: '8px' }}
                                                    checkedChildren={capitalize(day).substring(0, 3)}
                                                    unCheckedChildren={capitalize(day).substring(0, 3)}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Col> }
                            {/* Exceptions Section */}
                            <Col xs={24} lg={24}>
                                {!schedule.shared && <Form.Group className="mb-2" controlId="exceptions">
                                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '5px' }}>
                                        <Form.Label className="mb-0">Exceptions</Form.Label>
                                        <Button type="primary" className="icon-button btn-sm" icon={<PlusOutlined />} onClick={() => handleAddException('include')}>Add Exception</Button>
                                    </div>
                                    {schedule.exceptions?.length ? <div className="schedule-exceptions-list">
                                        {schedule.exceptions?.map((exception, idx) => (
                                            <div key={exception.id} className={`schedule-exception ${exception.type === 'include' ? 'schedule-include' : 'schedule-exclude'}`}>
                                                <div className="d-flex justify-content-between w-100">
                                                    <Select
                                                        style={{ minWidth: '100px' }}
                                                        size="small"
                                                        value={exception.type}
                                                        onChange={val => {
                                                            updateScheduleAndNotify(schedule => {
                                                                const updated = { ...schedule, exceptions: schedule.exceptions.slice() };
                                                                updated.exceptions[idx].type = val;
                                                                return updated;
                                                            });
                                                        }}
                                                    >
                                                        <Option value="include">Include</Option>
                                                        <Option value="exclude">Exclude</Option>
                                                    </Select>
                                                    <div className="d-flex justify-content-center align-items-center sb-edit-controls">
                                                        <Button className="sb-close" icon={<Close />}
                                                            onClick={() => {
                                                                updateScheduleAndNotify(schedule => {
                                                                    const updated = { ...schedule, exceptions: schedule.exceptions.slice() };
                                                                    updated.exceptions.splice(idx, 1);
                                                                    return updated;
                                                                });
                                                            }} />
                                                    </div>
                                                </div>
                                                <RangeOrPrefilledPicker
                                                    schedule={schedule}
                                                    disabled={schedule.shared && !isAdmin}
                                                    prefilledPeriods={prefilledPeriods}
                                                    value={exception.period ? new Period(exception.period) : exception.scheduleId ? prefilledPeriods.find(p => p.scheduleId === exception.scheduleId)?.schedule : null}
                                                    onChange={(val) => {
                                                        updateScheduleAndNotify(schedule => ({
                                                            ...schedule,
                                                            exceptions: schedule.exceptions.map((e, i) => {
                                                                if (i === idx) {
                                                                    if (val.type === 'range') {
                                                                        return {
                                                                            ...e,
                                                                            period: new Period(val.range)
                                                                        }
                                                                    } else if (val.type === 'prefilled') {
                                                                        return {
                                                                            ...e,
                                                                            scheduleId: val.prefilled.scheduleId,
                                                                            source: val.prefilled.label,
                                                                            period: null
                                                                        }
                                                                    }
                                                                }
                                                                return e;
                                                            })
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div> : <></>}
                                </Form.Group>}
                                {schedule.shared && <Form.Group className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '5px' }}>
                                        <Form.Label className="mb-0">Included Schedule</Form.Label>
                                        <Button type="primary" className="icon-button btn-sm" icon={<PlusOutlined />} onClick={() => handleAddPeriods()}>Add Period</Button>
                                    </div>
                                    <div className="schedule-exceptions-list">
                                        {schedule.schedulePeriods?.map((schedulePeriod, idx) => (
                                            <div key={schedulePeriod.id} className={`schedule-exception`}>
                                                <RangePicker disabled={schedule.shared && !isAdmin} className="w-100" style={{ maxWidth: '500px' }}
                                                    onChange={(value) => {
                                                        updateScheduleAndNotify(schedule => ({
                                                            ...schedule,
                                                            schedulePeriods: schedule.schedulePeriods.map((e, i) => {
                                                                if (i === idx) {
                                                                    return new Period(value);
                                                                }
                                                                return e;
                                                            })
                                                        }));
                                                    }}
                                                    value={[schedulePeriod.start, schedulePeriod.end]}
                                                    format={"DD/MM/YYYY"}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </Form.Group>}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </EditItemView>

            <Modal
                show={showPreview}
                onHide={() => setShowPreview(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Schedule Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ScheduleCalendar
                        schedulePeriods={schedule.schedulePeriods}
                        operatingDays={schedule.operatingDays}
                        excludedPeriods={[]} // No longer used in UI
                        includedSchedules={schedule.includedSchedules}
                        excludedSchedules={schedule.excludedSchedules}
                        runningDates={runningDates}
                        calendarTitle="Schedule Calendar Preview"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default React.memo(EditScheduleView);